import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';

const LanguageSwitcher: React.FC = () => {
  const { i18n: i18nInstance } = useTranslation();
  const [englishEnabled, setEnglishEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Загружаем параметр english_language_enabled
    const fetchEnglishLanguageEnabled = async () => {
      try {
        // Используем публичный endpoint для получения параметра локализации
        const response = await apiService.get<ApiResponse<{ name: string; value: boolean }>>(
          API_ENDPOINTS.SYSTEM_PARAMETERS.ENGLISH_LANGUAGE_ENABLED
        );

        // Структура ответа: { data: { status: 'success', data: { name: '...', value: ... } } }
        const responseData = response.data as any;
        if (responseData && responseData.data && responseData.data.value !== undefined) {
          const value = responseData.data.value;
          // Значение уже преобразовано в boolean на сервере
          setEnglishEnabled(value === true);
        } else {
          // По умолчанию английский язык выключен
          setEnglishEnabled(false);
        }
      } catch (error: any) {
        logger.error('Error fetching english_language_enabled parameter:', error);
        // В случае ошибки английский язык выключен по умолчанию
        setEnglishEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    fetchEnglishLanguageEnabled();
  }, []);

  const handleLanguageChange = (value: string) => {
    i18nInstance.changeLanguage(value);
    // i18next автоматически сохраняет выбор языка в localStorage
  };

  // Формируем список доступных языков
  const languageOptions = [
    { label: '🇷🇺 Русский', value: 'ru' },
  ];

  // Добавляем английский язык только если он включен
  if (englishEnabled) {
    languageOptions.push({ label: '🇬🇧 English', value: 'en' });
  }

  // Если английский язык выключен и текущий язык - английский, переключаем на русский
  useEffect(() => {
    if (!loading && !englishEnabled && i18nInstance.language === 'en') {
      i18nInstance.changeLanguage('ru');
    }
  }, [englishEnabled, loading, i18nInstance]);

  // Если параметр еще загружается, показываем только русский язык
  if (loading) {
    return (
      <Select
        value="ru"
        disabled
        style={{ width: 130, marginRight: 16 }}
        suffixIcon={<GlobalOutlined />}
        options={[{ label: '🇷🇺 Русский', value: 'ru' }]}
      />
    );
  }

  return (
    <Select
      value={i18nInstance.language || 'ru'}
      onChange={handleLanguageChange}
      style={{ width: 130, marginRight: 16 }}
      suffixIcon={<GlobalOutlined />}
      options={languageOptions}
    />
  );
};

export default LanguageSwitcher;
